/* ============================================================================
   Access requests — raised from the landing page, decided by the manager in
   /access. Synced to D1 as a single "meta" aggregate so requests reach the
   manager from any browser.
   ========================================================================== */
import { useEffect, useState } from "react";
import { STUDIO, uid } from "./store";
import { notify } from "./notify";
import type { AccessRequest } from "./types";

const KEY = "atelier.access.v1";
export const ACCESS_AGG_ID = "access-requests";

type State = { requests: AccessRequest[] };

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { requests: [] };
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(asAggregate());
}

export function useAccessRequests(): AccessRequest[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state.requests;
}
export function getAccessRequests() { return state.requests; }
export function pendingAccessCount() { return state.requests.filter((r) => r.status === "pending").length; }

/* --- sync hooks --------------------------------------------------------- */
export type AccessAggregate = { id: string; requests: AccessRequest[] };
function asAggregate(): AccessAggregate { return { id: ACCESS_AGG_ID, requests: state.requests }; }
let changeListener: ((a: AccessAggregate) => void) | null = null;
export function onAccessChange(f: (a: AccessAggregate) => void) { changeListener = f; }
export function getAccessAggregates(): AccessAggregate[] { return [asAggregate()]; }
export function replaceAccessAggregates(aggs: AccessAggregate[]) {
  const row = aggs.find((a) => a.id === ACCESS_AGG_ID);
  if (!row) return;
  state = { requests: row.requests ?? [] };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- actions --------------------------------------------------------------- */
export const accessActions = {
  submit(data: { name: string; company: string; email: string; desired: AccessRequest["desired"]; targetKind: "tender" | "project"; targetId: string; targetLabel: string; role?: AccessRequest["role"]; message?: string }) {
    const r: AccessRequest = { id: uid("ar-"), status: "pending", createdAt: new Date().toISOString(), ...data };
    commit({ requests: [r, ...state.requests] });
    notify(STUDIO.managerEmail,
      `Access request — ${data.company} (${data.desired}${data.role ? ` as ${data.role}` : ""}, ${data.targetLabel})`,
      `${data.name} of ${data.company} <${data.email}> requests ${data.desired} access${data.role ? ` as ${data.role}` : ""} to ${data.targetLabel}.${data.message ? ` Message: ${data.message}` : ""} Approve it in the Access section.`);
    return r;
  },
  approve(id: string, code: string) {
    const r = state.requests.find((x) => x.id === id);
    if (!r) return;
    commit({ requests: state.requests.map((x) => x.id === id ? { ...x, status: "approved" as const, code, decidedAt: new Date().toISOString() } : x) });
    notify([r.email],
      `Access approved — ${r.targetLabel}`,
      `${STUDIO.name} ${STUDIO.unit}: your ${r.desired} access to ${r.targetLabel} is approved. Sign in with access code ${code}.`);
  },
  decline(id: string) {
    const r = state.requests.find((x) => x.id === id);
    if (!r) return;
    commit({ requests: state.requests.map((x) => x.id === id ? { ...x, status: "declined" as const, decidedAt: new Date().toISOString() } : x) });
    notify([r.email],
      `Access request — ${r.targetLabel}`,
      `${STUDIO.name} ${STUDIO.unit}: your access request for ${r.targetLabel} was not approved. Reply to this email if you believe this is an error.`);
  },
};
